import { GraphQLClient } from 'graphql-request';
import {
    vippsMutation,
    avtalegiroMutation,
    stripeMutation,
    stripePublicKeyQuery,
    vippsPaymentInitiateMutation,
    vippsPaymentProfileInitiateMutation,
    stripePaymentIntentCreateMutation,
    stripePaymentIntentConfirmMutation
} from './queries';

class Form {
    constructor (container) {
        this.apiUrl = window.cornerstoneApiUrl;

        // Get the container element, and the form elements.
        this.container = container;
        this.outerContainer = this.container.closest('.donor-form__container-outer');
        this.form = this.container.querySelector('.donor-form__form');
        this.formDetails = this.container.querySelector('.donor-form__form-information');
        this.formStripe = this.container.querySelector('.donor-form__form-stripe');

        // All submit buttons.
        this.formButtons = this.container.querySelectorAll('button[type="submit"]');

        // Add event listeners for submitting.
        this.form.addEventListener('submit', this.submit.bind(this)); // First form (vipps, avtalegiro or stripe)
        this.formDetails.addEventListener('submit', this.submitDetails.bind(this)); // Second form (avtalegiro or stripe).
        this.formStripe.addEventListener('submit', this.submitStripe.bind(this)); // Third form (only stripe).

        // We must know what button triggers the form submit (only on the FIRST (!) form, as the buttons represent a choice).
        this.submitInitiator = null;
        this.firstFormSubmitButtons = this.form.querySelectorAll('button[type="submit"]');
        for (const btn of this.firstFormSubmitButtons) {
            btn.addEventListener('click', this.submitButtonClick.bind(this));
        }

        // We can go back on the second and third form, so we need to add an event listener to the back button.
        this.backButton = this.formDetails.querySelector('.donor-form-button__back.avtalegiro');
        this.backButton.addEventListener('click', this.back.bind(this));
        this.backButtonStripe = this.formStripe.querySelector('.donor-form-button__back.stripe');
        this.backButtonStripe.addEventListener('click', this.back.bind(this));

        // We need to hide switch buttons if the user changes recurrance.
        this.recurringRadios = this.container.querySelectorAll('.donor-form__recurring input[name="recurrence"]');
        for (const btn of this.recurringRadios) {
            btn.addEventListener('click', this.recurringRadioClick.bind(this));
        }

        // We need to handle the amount inputs.
        this.amount = parseInt(this.container.querySelector('.donor-form__amount input[name="amount"]:checked').value); // Default value.
        this.customAmount = this.container.querySelector('.donor-form__amount input[name="amountCustom"]');
        this.amountRadios = this.container.querySelectorAll('.donor-form__amount input[name="amount"]');
        this.customAmount.addEventListener('input', this.customAmountInput.bind(this));
        for (const btn of this.amountRadios) {
            btn.addEventListener('click', this.amountRadioClick.bind(this));
        }

        // Stripe card element.
        this.stripeCardElement = this.formStripe.querySelector('.stripe-card-element');

        // Listen to all input changes
        this.inputs = this.container.querySelectorAll('input');
        for (const input of this.inputs) {
            input.addEventListener('change', this.inputChange.bind(this));
        }

        // Lock the form container height, so that the form doesn't jump around when we change the content.
        if (!this.outerContainer.classList.contains('no-image')) {
            // Only do this if the form has an image
            this.outerContainer.style.height = this.outerContainer.offsetHeight + 'px';
        }

        // Bind this to all functions.
        this.setAmountFeedback = this.setAmountFeedback.bind(this);
        this.submitData = this.submitData.bind(this);
        this.toggleSubmitButtonsDisabled = this.toggleSubmitButtonsDisabled.bind(this);
        this.setError = this.setError.bind(this);
        this.inputChange = this.inputChange.bind(this);
        this.cardElementListener = this.cardElementListener.bind(this);
        this.queryAvtaleGiro = this.queryAvtaleGiro.bind(this);
        this.queryVipps = this.queryVipps.bind(this);
        this.queryStripe = this.queryStripe.bind(this);
        this.confirmStripePaymentIntent = this.confirmStripePaymentIntent.bind(this);
    }

    /**
     * When the user changes the card element input (cardnumber, cvc, etc), we need to update the error message.
     */
    cardElementListener () {
        // Listen to changes in the card element.
        this.cardElement.on('change', (event) => {
            this.setError(''); // Clear error message.
        });
    }

    /**
     * On the first form, the submit buttons represent a choice. This function is
     * triggered when a submit-button (in the first form) is clicked, which
     * triggers BEFORE the submit-event (!), which then will use the submitInitiator-
     * variable from this function.
     * 
     * @param {*} e 
     */
    submitButtonClick (e) {
        // Triggered before the submit event, so we can set the submitInitiator here.
        this.submitInitiator = e.currentTarget; // vipps or avtalegiro
    }

    /**
     * Handles submit-event for the first form, where the user chooses between Vipps and AvtaleGiro.
     * 
     * @param {*} e - Event
     */
    submit (e) {
        e.preventDefault();

        // console.log({submitInitiator: this.submitInitiator});

        const type = this.submitInitiator.dataset.type;

        if (type === 'vipps') {
            // Use Vipps.
            this.submitData('vipps', this.submitInitiator);
        } else {
            // Use AvtaleGiro or Stripe.
            // Show the details form.
            this.form.classList.remove('active');
            this.formDetails.classList.add('active');

            // Change the submit button text.
            const detailsButton = this.formDetails.querySelector('button[type="submit"]').querySelector('.default');
            detailsButton.innerText = type === 'stripe' ? 'Gå videre' : 'Aktiver AvtaleGiro';

            // We have to hide the feedback message if Stripe, as it's not relevant the second form (it is only necessary on the second form, if the user is using AvtaleGiro, as it states the recurring amount).
            const feedbackMessage = this.formDetails.querySelector('.donor-form__form-information-text');
            if (type === 'stripe') {
                // Stripe. Hide the feedback message, as it's not relevant.
                feedbackMessage.style.display = 'none';
            } else {
                // AvtaleGiro. Show the feedback message. Show the feedback message, as it's relevant.
                feedbackMessage.style.display = 'block';
            }
        }
    }

    /**
     * Handles submit-event for the second form, where the user enter details for the AvtaleGiro agreement.
     * 
     * @param {*} e - Event
     */
    async submitDetails (e) {
        e.preventDefault();

        // Check if submitInitiator is Stripe or AvtaleGiro.
        const submitButton = this.formDetails.querySelector('button[type="submit"]');

        if (this.submitInitiator.dataset.type === 'stripe') {
            // Use Stripe.

            try {
                // 1. Submit the data here, as it will create invoiceEncodedId
                const res = await this.submitData('stripe', submitButton);
                if (res.error) {
                    // console.log({error: res.error});
                    this.setError('Det oppstod en feil ved oppretting av avtale. Prøv igjen senere.');
                } else {
                    // 2. submitData keeps the invoiceEncodedId in a variable (this.invoiceEncodedId), and then we display the Stripe form here.
                    this.formDetails.classList.remove('active');
                    this.formStripe.classList.add('active');

                    // // 3. Load Stripe.
                    // if (!this.stripe || !this.cardElement) {
                    //     // Only load Stripe and create cardElement if it hasn't been loaded already.
                    //     const pk = this.submitReturnData.stripePublicKey;
                    //     this.stripe = await loadStripe(pk, {locale: 'nb'});
                    //     const elements = this.stripe.elements();
                    //     this.cardElement = elements.create('card');
                    //     this.cardElement.mount(this.stripeCardElement);

                    //     this.cardElementListener();
                    // }
                }
            } catch (error) {
                // console.log({error});
                this.setError('Det oppstod en feil ved oppretting av avtale. Prøv igjen senere.');
            }
            
        } else {
            // Use AvtaleGiro.
            // Submit the data.
            
            this.submitData('avtalegiro', submitButton);
        }
    }

    /**
     * Handles submit-event for the third form, where the user enter card-details.
     * 
     * @param {*} e - Event
     */
    async submitStripe (e) {
        e.preventDefault();

        this.setError(''); // Clear error message.
        // console.log({submitStripe: this.cardElement});

        // Disable the submit buttons and show a loading indicator.
        const submitButton = this.formStripe.querySelector('button[type="submit"]');
        this.toggleSubmitButtonsDisabled(true, submitButton);

        // 1. Create the payment method in stripe
        const name = this.formDetails.querySelector('input[name="name"]').value
        const result = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardElement,
            billing_details: {
                name: name,
            },
        })

        // console.log({stripeRes: result});
        if (result.error) {
            // Show error in payment form
            this.setError(result.error.message);
            this.toggleSubmitButtonsDisabled(false, submitButton);
            
            return;
        }

        const paymentMethodId = result.paymentMethod.id;
        const invoiceEncodedId = this.submitReturnData.invoiceEncodedId;

        // 2. Use the paymentMethodId and the invoiceEncodedId from previous step to fulfill the payment in Cornerstone using the paymentMedthodId from Stripe.
        const graphQLClient = new GraphQLClient(this.apiUrl);
        const data = await graphQLClient.request(stripePaymentIntentCreateMutation, {
            invoiceEncodedId,
            stripePaymentMethodId: paymentMethodId
        });

        if (data.room.payment.stripePaymentIntentCreate.userErrors.length > 0) {
            this.setError(data.room.payment.stripePaymentIntentCreate.userErrors[0].message);
            this.toggleSubmitButtonsDisabled(false, submitButton);

            return;
        }
        
        // console.log({stripePaymentIntentCreateMutation: data});

        // 3. Handle required actions from stripe.
        if (data.room.payment.stripePaymentIntentCreate.paymentIntent && data.room.payment.stripePaymentIntentCreate.paymentIntent.status === 'succeeded') {
            // Success!

            const redirectUrl = data.room.payment.stripePaymentIntentCreate.invoice.successUrl;
            // console.log({redirectUrl});
            location.href = redirectUrl;
        } else {
            // requires_action
            // console.log('requires_action');
            try {
                const actionCompleted = await this.stripe.handleCardAction(data.room.payment.stripePaymentIntentCreate.paymentIntent.clientSecret);
                // console.log({actionCompleted});

                if (actionCompleted.error) {
                    this.setError(actionCompleted.error.message);
                } else {
                    await this.confirmStripePaymentIntent(invoiceEncodedId, actionCompleted.paymentIntent.id);
                }
            } catch (error) {
                // console.log({error});
                this.setError('Det oppstod en feil ved autentiseringen. Prøv igjen senere.');
            }

            this.toggleSubmitButtonsDisabled(false, submitButton);
        }
    }

    /**
     * In some cases Stripe will require additional actions from the user, such as 3D Secure. After the
     * user has completed the action, we need to call this function to complete the payment.
     * 
     * @param {*} invoiceEncodedId 
     * @param {*} paymentIntentId 
     */
    async confirmStripePaymentIntent(invoiceEncodedId, paymentIntentId) {
        const graphQLClient = new GraphQLClient(this.apiUrl);
        const data = await graphQLClient.request(stripePaymentIntentConfirmMutation, {
            invoiceEncodedId,
            paymentIntentId
        });
        // console.log({stripePaymentIntentConfirmMutation: data});

        if (data.room.payment.stripePaymentIntentConfirm.paymentIntent && data.room.payment.stripePaymentIntentConfirm.paymentIntent.status === 'succeeded') {
            // Success!
            // Redirect to the success page.
            const redirectUrl = data.room.payment.stripePaymentIntentConfirm.invoice.successUrl;
            location.href = redirectUrl;
        } else if (data.room.payment.stripePaymentIntentConfirm.userErrors && data.room.payment.stripePaymentIntentConfirm.userErrors.length > 0) {
            this.setError(data.room.payment.stripePaymentIntentConfirm.userErrors[0].message);
        } else {
            this.setError('Det oppstod en feil ved godkjenningen. Prøv igjen senere.');
        }
    }

    /**
     * Submit data to Cornerstone API to create a new donor.
     * 
     * @param {*} type - vipps or avtalegiro
     */
    async submitData (type, submitButton) {
        // Send data to the server.

        this.setError(''); // Clear error message.

        // Disable the submit buttons and show a loading indicator.
        this.toggleSubmitButtonsDisabled(true, submitButton);

        const recurrence = this.container.querySelector('.donor-form__recurring input[name="recurrence"]:checked').value;
        const amount = this.amount;

        // Assemble the data.
        const causeId = recurrence === 'NOT_RECURRENT' ? this.container.dataset.causeSingle : this.container.dataset.causeRecurrent;
        const successPageId = recurrence === 'NOT_RECURRENT' ? this.container.dataset.successPageSingle : this.container.dataset.successPageRecurrent;
        const cancelPageId = window.cornerstoneCancelPageId;

        // Get the donor details.
        const name = this.formDetails.querySelector('input[name="name"]').value.split(' ');
        const firstName = name[0] || null;
        name.shift(); // Remove the first name from the array.
        const middleName = name.length > 1 ? name[0] : null; // If the name has more than one word, the first word is the middle name.
        if (middleName) name.shift(); // Remove the middle name from the array.
        const lastName = name.join(' ') || null; // The rest of the words is the last name.

        const ssn = this.formDetails.querySelector('input[name="ssn"]').value || null;

        const phone = this.formDetails.querySelector('input[name="phone"]').value;
        const email = this.formDetails.querySelector('input[name="email"]').value;

        // Payment options.
        const vipps = window.cornerstoneVippsId;
        // const stripe = window.cornerstoneStripeId;
        // const avtalegiro = window.cornerstoneAvtalegiroId;

        const paymentOptionId = vipps;
        // const paymentOptionId = type === 'vipps'
        //     ? vipps
        //     : type === 'stripe'
        //     ? stripe
        //     : avtalegiro;

        const q = this.queryVipps;
        // // Query to send to the server.
        // const q = type === 'vipps'
        //     ? this.queryVipps
        //     : type === 'stripe'
        //     ? this.queryStripe
        //     : this.queryAvtaleGiro;

        try {
            const data = await q({
                amount: parseInt(amount),
                causeId: parseInt(causeId),
                paymentOptionId: parseInt(paymentOptionId),
                recurrence,
                email,
                phone,
                first: firstName,
                last: lastName,
                middle: middleName,
                successPageId: parseInt(successPageId),
                cancelPageId: parseInt(cancelPageId),
                ssn
            });
            // console.log({result: data});

            // Save the return data in a variable, so we can use it later.
            this.submitReturnData = data;

            // Track the donation. We're tracking before the user pays, so admin can see how many people are intending to donate, and how many actually donate (by checking the success page visits).
            window.plausible(recurrence === 'NOT_RECURRENT' ? 'Enkeltbidrag - ' + type : 'Fastgiver - ' + type, {
                callback: function () {
                    // Handle the response.
                    if (type === 'stripe') {
                        // Don't do anything here, as we need to do more steps to complete the payment.
                    } else {
                        // Redirect to the redirectUrl for Vipps and AvtaleGiro-payment.
                        location.href = data.redirectUrl;
                    }
                }
            });
            
            this.toggleSubmitButtonsDisabled(false, submitButton);

            return {error: null, data};
        } catch (error) {
            // console.log({error});

            this.setError('Det oppstod en feil. Prøv igjen senere.');
            this.toggleSubmitButtonsDisabled(false, submitButton);

            return {error};
        }
    }

    /**
     * Listen to all input changes.
     */
    inputChange () {
        // Clear error message.
        this.setError('');
    }

    /**
     * On the second/third form, the user can go back to the first form.
     */
    back (e) {
        if (e.currentTarget.classList.contains('stripe')) {
            // Go back to the second form.
            this.formDetails.classList.add('active');
            this.formStripe.classList.remove('active');
        } else {
            // Go back to the first form.
            this.formDetails.classList.remove('active');
            this.form.classList.add('active');
        }
    }

    /**
     * Handles input-event for the custom amount input.
     */
    customAmountInput (e) {
        const value = e.target.value;
        const amount = value.replace(/[^0-9]/g, '');
        e.target.value = amount;
        this.customAmount.classList.add('active');

        this.amount = parseInt(amount);

        // Uncheck all the other amount radios.
        this.amountRadios.forEach(radio => {
            radio.checked = false;
        });

        // Set the amount feedback.
        this.setAmountFeedback(amount);
    }

    /**
     * Click event for the amount radios.
     * @param {*} e 
     */
    amountRadioClick (e) {
        const amount = e.target.value;
        this.customAmount.value = ''; // Clear the custom amount input.
        this.customAmount.classList.remove('active');

        this.amount = parseInt(amount);

        // Set the amount feedback.
        this.setAmountFeedback(amount);
    }

    /**
     * Click event for the recurring radios.
     * @param {*} e 
     */
    recurringRadioClick (e) {
        const recurrence = e.target.value;
        if (recurrence === 'NOT_RECURRENT') {
            // this.form.querySelector('button[type="submit"][data-type="avtalegiro"]').classList.add('hidden'); // Hide the AvtaleGiro button.
            // this.form.querySelector('button[type="submit"][data-type="stripe"]').classList.remove('hidden'); // Show the Stripe button.

            // Set the amount radios to the single amount.
            this.amountRadios.forEach(radio => {
                radio.value = radio.dataset.single;
                const label = radio.nextElementSibling;
                label.innerText = radio.dataset.single + 'kr';
            });
            
        } else {
            // this.form.querySelector('button[type="submit"][data-type="avtalegiro"]').classList.remove('hidden'); // Show the AvtaleGiro button.
            // this.form.querySelector('button[type="submit"][data-type="stripe"]').classList.add('hidden'); // Hide the Stripe button.

            // Set the amount radios to the recurring amount.
            this.amountRadios.forEach(radio => {
                radio.value = radio.dataset.recurring;
                const label = radio.nextElementSibling;
                label.innerText = radio.dataset.recurring + 'kr';
            });
        }

        // Values of amount radios changed, so we need to check if a radio is checked, and if so update the current amount with it's value.
        const selectedAmount = this.container.querySelector('.donor-form__amount input[name="amount"]:checked');
        if (selectedAmount) {
            this.amount = parseInt(selectedAmount.value);
            this.setAmountFeedback(this.amount);
        }
    }

    /**
     * Set the amount feedback indicator.
     */
    setAmountFeedback (amount) {
        const feedback = this.container.querySelector('.donor-form__form-information-amount-indicator');
        feedback.innerHTML = amount;
    }

    /**
     * Set error message.
     */
    setError (message) {
        const error = this.container.querySelectorAll('.donor-form__feedback');
        
        error.forEach(element => {
            element.innerHTML = message;
            if (message) {
                element.classList.add('visible');
            } else {
                element.classList.remove('visible');
            }
        });
    }

    /**
     * Disable the submit buttons.
     */
    toggleSubmitButtonsDisabled (disabled = false, submitButton = null) {
        // Toggle the loading indicator.
        if (submitButton) {
            submitButton.querySelector('.loading').style.display = disabled ? 'inline-flex' : 'none';
            submitButton.querySelector('.default').style.display = disabled ? 'none' : 'inline-flex';
        }
        // Disable the submit buttons.
        this.formButtons.forEach(btn => {
            btn.disabled = disabled;
        });
    }

    /**
     * Run the giftPrepare mutation from the Cornerstone API. Using the invoiceEncodedId/papaymentProfileEncodedId, we can
     * run another query to get the redirectUrl. The URL handles the payment and donation. Will
     * then return to the success/cancel page.
     */
    async queryVipps (variables) {
        // console.log('queryVipps:', variables);
        const graphQLClient = new GraphQLClient(this.apiUrl);
        const data = await graphQLClient.request(vippsMutation, variables);
        
        const mutation = variables.recurrence === 'NOT_RECURRENT' ? vippsPaymentInitiateMutation : vippsPaymentProfileInitiateMutation; // Use the correct mutation.
        const vars = variables.recurrence === 'NOT_RECURRENT' ? {invoiceEncodedId: data.room.fundraising.giftPrepare.invoiceEncodedId} : {paymentProfileEncodedId: data.room.fundraising.giftPrepare.paymentProfileEncodedId};
        const initiate = await graphQLClient.request(mutation, vars); // Use the correct variables.

        // console.log({initiate});

        return {
            redirectUrl: variables.recurrence === 'NOT_RECURRENT' ? initiate.room.payment.vippsPaymentInitiate.redirectUrl : initiate.room.payment.vippsPaymentProfileInitiate.redirectUrl
        };
    }

    /**
     * Run the giftPrepare mutation from the Cornerstone API. It will return a
     * URL to redirect the user to, which handles the payment and donation. Will
     * then return to the success/cancel page.
     */
    async queryAvtaleGiro (variables) {
        // console.log('queryAvtaleGiro:', variables);

        const graphQLClient = new GraphQLClient(this.apiUrl);
        const data = await graphQLClient.request(avtalegiroMutation, variables);
        
        // console.log(JSON.stringify(data, undefined, 2));

        return {
            redirectUrl: data.room.fundraising.giftPrepare.redirectUrl
        };
    }

    /**
     * Run the giftPrepare mutation from the Cornerstone API. It will return a
     * URL to redirect the user to, which handles the payment and donation. Will
     * then return to the success/cancel page.
     */
    async queryStripe (variables) {
        // console.log('queryStripe:', variables);

        const graphQLClient = new GraphQLClient(this.apiUrl);
        const data = await graphQLClient.request(stripeMutation, variables);
        
        const stripePublicKey = await graphQLClient.request(stripePublicKeyQuery, {encodedId: data.room.fundraising.giftPrepare.invoiceEncodedId});

        // console.log({stripePublicKey});
        
        return {
            redirectUrl: null,
            invoiceEncodedId: data.room.fundraising.giftPrepare.invoiceEncodedId,
            stripePublicKey: stripePublicKey.room.paymentInvoiceByEncodedId && stripePublicKey.room.paymentInvoiceByEncodedId.module.paymentOptions.items.length
                ? stripePublicKey.room.paymentInvoiceByEncodedId.module.paymentOptions.items[0].publicKey
                : null
        };
    }
}
export default Form;